import React, { useCallback } from 'react';
import type { Geometry } from 'geojson';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';

import { formatDateTime } from '../../helpers';
import { FieldSessionMap } from '../FieldSessionMap';
import {
  formatFieldSessionDistance,
  formatFieldSessionDuration,
} from './format';
import { useIntl } from 'react-intl';
import { IconButton } from '../design-system';

export {
  formatFieldSessionDistance,
  formatFieldSessionDuration,
} from './format';

export interface FieldSessionSummaryData {
  id: string;
  name?: string | null;
  note?: string | null;
  startedAt: string | Date;
  endedAt?: string | Date | null;
  siteId?: string | null;
  siteName?: string | null;
  activeDurationSeconds: number;
  distanceMeters?: number | null;
  findCount: number;
  track?: Geometry | null;
}

const rowStyles = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: 1,
  alignItems: 'stretch',
  p: 1.5,
  borderRadius: 2,
  cursor: 'pointer',
  transition: 'background-color 120ms ease',
  '&:hover, &:focus-visible, &:focus-within': {
    bgcolor: (theme: Theme) =>
      theme.palette.mode === 'light'
        ? alpha(theme.palette.common.black, 0.055)
        : alpha(theme.palette.common.white, 0.065),
    outline: 'none',
  },
  '&:hover .field-session-actions, &:focus-within .field-session-actions': {
    opacity: 1,
  },
};
const rowLinkStyles = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: '210px minmax(0, 1fr)',
  },
  gap: { xs: 1.5, sm: 2.5 },
  minWidth: 0,
  color: 'inherit',
  textDecoration: 'none',
  '&:focus-visible': { outline: 'none' },
};
const mapWrapperStyles = {
  aspectRatio: '16 / 9',
  minHeight: 112,
};
const contentStyles = { minWidth: 0, py: 0.5 };
const titleStyles = { fontSize: '1.08rem', fontWeight: 600, lineHeight: 1.3 };
const metadataGridStyles = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
  },
  gap: 1.5,
  mt: 2,
};
const actionsStyles = {
  alignSelf: 'center',
  opacity: { xs: 1, sm: 0 },
  transition: 'opacity 120ms ease',
};

function SessionFact({ label, value }: { label: string; value: string }) {
  return (
    <Stack gap={0.15} minWidth={0}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" noWrap title={value}>
        {value}
      </Typography>
    </Stack>
  );
}

export const FieldSessionSummary = React.memo(
  ({ session }: { session: FieldSessionSummaryData }) => {
    const intl = useIntl();
    const isActive = !session.endedAt;
    const date = formatDateTime(session.startedAt);
    const siteName =
      session.siteName ||
      intl.formatMessage({
        id: 'fieldSessions.noSiteSelected',
        defaultMessage: 'No site selected',
      });
    const findLabel = intl.formatMessage(
      {
        id: 'fieldSessions.findCount',
        defaultMessage: '{count, plural, one {# find} other {# finds}}',
      },
      { count: session.findCount }
    );
    const notRecordedLabel = intl.formatMessage({
      id: 'fieldSessions.notRecorded',
      defaultMessage: 'Not recorded',
    });

    return (
      <Box sx={contentStyles}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography component="h2" noWrap sx={titleStyles}>
            {session.name ||
              intl.formatMessage({
                id: 'fieldSessions.untitled',
                defaultMessage: 'Untitled field session',
              })}
          </Typography>
          {isActive && (
            <Chip
              size="small"
              color="success"
              label={intl.formatMessage({
                id: 'fieldSessions.active',
                defaultMessage: 'Active',
              })}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" mt={0.75}>
          {date} · {siteName}
        </Typography>
        <Box sx={metadataGridStyles}>
          <SessionFact
            label={intl.formatMessage({
              id: 'fieldSessions.trackLength',
              defaultMessage: 'Track length',
            })}
            value={formatFieldSessionDistance(
              session.distanceMeters,
              notRecordedLabel
            )}
          />
          <SessionFact
            label={intl.formatMessage({
              id: 'fieldSessions.duration',
              defaultMessage: 'Duration',
            })}
            value={formatFieldSessionDuration(session.activeDurationSeconds)}
          />
          <SessionFact
            label={intl.formatMessage({
              id: 'fieldSessions.finds',
              defaultMessage: 'Finds',
            })}
            value={findLabel}
          />
        </Box>
      </Box>
    );
  }
);

interface FieldSessionRowProps {
  session: FieldSessionSummaryData;
  deleting: boolean;
  onDelete: (session: FieldSessionSummaryData) => void;
}

const FieldSessionRow = React.memo(
  ({ session, deleting, onDelete }: FieldSessionRowProps) => {
    const intl = useIntl();
    const location = useLocation();
    const detailPath = `/field-sessions/${session.id}${location.search}`;

    const handleDelete = useCallback(() => {
      onDelete(session);
    }, [onDelete, session]);

    return (
      <Box sx={rowStyles}>
        <Box
          component={Link}
          to={detailPath}
          aria-label={intl.formatMessage(
            {
              id: 'fieldSessions.open',
              defaultMessage: 'Open {name}',
            },
            {
              name:
                session.name ||
                intl.formatMessage({
                  id: 'fieldSessions.genericName',
                  defaultMessage: 'field session',
                }),
            }
          )}
          sx={rowLinkStyles}
        >
          <Box sx={mapWrapperStyles}>
            <FieldSessionMap track={session.track} compact />
          </Box>
          <FieldSessionSummary session={session} />
        </Box>
        <Box className="field-session-actions" sx={actionsStyles}>
          <IconButton
            aria-label={intl.formatMessage(
              {
                id: 'fieldSessions.delete',
                defaultMessage: 'Delete {name}',
              },
              {
                name:
                  session.name ||
                  intl.formatMessage({
                    id: 'fieldSessions.genericName',
                    defaultMessage: 'field session',
                  }),
              }
            )}
            density="compact"
            disabled={deleting}
            onClick={handleDelete}
            tone="danger"
            variant="ghost"
          >
            {deleting ? <CircularProgress size={20} /> : <DeleteOutlineIcon />}
          </IconButton>
        </Box>
      </Box>
    );
  }
);

interface FieldSessionListProps {
  sessions: FieldSessionSummaryData[];
  deletingId?: string | null;
  onDelete: (session: FieldSessionSummaryData) => void;
}

export function FieldSessionList({
  sessions,
  deletingId,
  onDelete,
}: FieldSessionListProps) {
  return (
    <Stack gap={0.5}>
      {sessions.map((session) => (
        <FieldSessionRow
          key={session.id}
          session={session}
          deleting={deletingId === session.id}
          onDelete={onDelete}
        />
      ))}
    </Stack>
  );
}
